import React, { useState } from 'react';
import fr from 'facet-rest';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { DialogContent } from 'Views/Page/Content/Modal/Components';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import PropTypes from 'prop-types';
import {
  FDBox,
  FDListItemAvatar,
  FDStack,
} from '@facet-wealth/design';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useSurveyContext } from 'Views/Onboarding/Pages/Survey/SurveyContext';
import MetaLayoutRemainingFields from 'Components/UI/Meta/MetaForm/Components/MetaLayout/MetaLayoutRemainingFields';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import AssetAndLiabilityDialogActions
  from 'Views/Page/Content/Templates/DigitalOnboardingRealAssetInfo/AssetAndLiabilityDialogActions';
import { dataAttrs } from 'Helpers/Testing';

const DigitalOnboardingAccountInfoTemplate = ({ isPartOfTypeStep }) => {
  const { loadModalCollectionByLink } = useModalContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { refreshOnboardingStepCollection } = useSurveyContext();
  const [loading, setLoading] = useState(false);
  const {
    onSubmit, saving, resource,
  } = useMetaLayoutContext();

  const handleSaveAndClose = async () => {
    try {
      setLoading(true);
      const resp = await onSubmit();
      if (resp) {
        await refreshOnboardingStepCollection();
      }
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Error saving');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndAddAnother = async (rel) => {
    try {
      setLoading(true);
      await resource.submit();
      await loadModalCollectionByLink(rel.link('related').url());
      refreshOnboardingStepCollection(); // Not awaiting because the user should be able to interact with the new form before we finish refreshing - we can do this refresh in the background
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Error saving');
    } finally {
      setLoading(false);
    }
  };

  const changeTypeRel = resource.withRelationship(fr.relationships.changeType.name, (rel) => rel, () => null);
  const handleChangeType = async () => {
    try {
      setLoading(true);
      await loadModalCollectionByLink(changeTypeRel.link('related').url());
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Error changing type');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      { !isPartOfTypeStep ? (
        <FDStack direction={'column'}>
          {resource.withRelationship(
            fr.relationships.logo.name,
            (rel) => (
              <FDBox padding={3} maxWidth={'200px'}>
                <FDListItemAvatar
                  src={rel.link('related').url()}
                  alt={'Institution logo'}
                />
              </FDBox>
            ),
            () => null,
          )}
          <MetaControlField attr="title" simpleRootStyle />
        </FDStack>
      ) : null}
      <DialogContent>
        <FDStack direction={'column'} alignItems={'stretch'} spacing={2} {...dataAttrs('digital-onboarding-account-info-template')}>
          <MetaControlField attr={'name'} simpleRootStyle />
          <MetaControlField attr={'searchableFinancialInstitutionID'} simpleRootStyle />
          <MetaControlField attr={'otherFinancialInstitution'} />
          <MetaControlField attr={'balance'} simpleRootStyle />
          <MetaControlField attr={'ownerClientId'} />
          <MetaControlField attr={'owner'} />
          <MetaLayoutRemainingFields noAccordion />
        </FDStack>
      </DialogContent>
      { !isPartOfTypeStep
        ? <AssetAndLiabilityDialogActions
          addAnotherRel={resource.relationship(fr.relationships.addManualAccount.name)}
          handleSaveAndAddAnother={handleSaveAndAddAnother}
          handleSaveAndClose={handleSaveAndClose}
          handleChangeType={handleChangeType}
          changeTypeRel={changeTypeRel}
          isLoading={loading || saving}
          isDisabled={false}
          useFD={false}
        /> : null}
    </>
  );
};

DigitalOnboardingAccountInfoTemplate.defaultProps = {
  isPartOfTypeStep: false,
};

DigitalOnboardingAccountInfoTemplate.propTypes = {
  isPartOfTypeStep: PropTypes.bool,
};

export default DigitalOnboardingAccountInfoTemplate;
